import { createTheme } from '@mantine/core';

type MantineColorsTuple = [string, string, string, string, string, string, string, string, string, string];

const darkBlue: MantineColorsTuple = [
  '#e6f3ff',
  '#cce4ff',
  '#99c7ff',
  '#66aaff',
  '#338dff',
  '#0070ff',
  '#0056cc',
  '#003d99',
  '#002366',
  '#000a33'
];

export const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    dark: [
      '#e2e8f0', // text
      '#cbd5e1',
      '#94a3b8',
      '#64748b', // muted
      '#475569',
      '#334155',
      '#1e293b',
      '#0f172a', // panel
      '#0b1220', // bg
      '#020617'
    ],
    blue: darkBlue
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  fontSizes: {
    xs: 'clamp(0.7rem, 2vw, 0.75rem)',
    sm: 'clamp(0.8rem, 2.2vw, 0.875rem)',
    md: 'clamp(0.9rem, 2.5vw, 1rem)',
    lg: 'clamp(1rem, 3vw, 1.125rem)',
    xl: 'clamp(1.1rem, 3.5vw, 1.25rem)'
  },
  spacing: {
    xs: 'clamp(0.25rem, 1vw, 0.5rem)',
    sm: 'clamp(0.5rem, 1.5vw, 0.75rem)',
    md: 'clamp(0.75rem, 2vw, 1rem)',
    lg: 'clamp(1rem, 3vw, 1.5rem)',
    xl: 'clamp(1.5rem, 4vw, 2rem)'
  },
  radius: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem'
  },
  breakpoints: {
    xs: '20em', // 320px - old phones
    sm: '30em', // 480px - phones
    md: '48em', // 768px - tablets
    lg: '64em', // 1024px - small laptops
    xl: '80em'  // 1280px - desktops
  },
  components: {
    Button: {
      defaultProps: {
        size: 'md',
        radius: 'md'
      },
      styles: {
        root: {
          minHeight: '44px',
          minWidth: '44px',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'rgba(96, 165, 250, 0.3)',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          cursor: 'pointer',
          // iOS-specific button fixes
          WebkitAppearance: 'none',
          appearance: 'none',
          border: 'none',
          outline: 'none'
        }
      }
    },
    ActionIcon: {
      styles: {
        root: {
          minHeight: '44px',
          minWidth: '44px',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'rgba(96, 165, 250, 0.3)'
        }
      }
    },
    Modal: {
      defaultProps: {
        centered: true,
        overlayProps: { backgroundOpacity: 0.6, blur: 3 },
        radius: 'md',
        size: 'xl'
      },
      styles: {
        root: {
          zIndex: 1000
        },
        content: {
          backgroundColor: 'var(--mantine-color-dark-7)',
          border: '1px solid var(--mantine-color-dark-6)',
          borderRadius: 'var(--mantine-radius-md)',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
          maxHeight: '90vh',
          overflow: 'hidden'
        },
        header: {
          backgroundColor: 'var(--mantine-color-dark-7)',
          borderBottom: '1px solid var(--mantine-color-dark-6)',
          padding: 'var(--mantine-spacing-md)',
          margin: 0
        },
        title: {
          fontSize: 'var(--mantine-font-size-lg)',
          fontWeight: 600,
          color: 'var(--mantine-color-gray-0)',
          margin: 0
        },
        body: {
          padding: 'var(--mantine-spacing-md)',
          maxHeight: 'calc(90vh - 120px)',
          overflow: 'auto'
        },
        close: {
          color: 'var(--mantine-color-gray-4)',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-dark-6)',
            color: 'var(--mantine-color-gray-2)'
          }
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(3px)'
        }
      }
    },
    AppShell: {
      styles: {
        root: {
          height: '100vh', // Use standard vh for iOS compatibility
          minHeight: '-webkit-fill-available', // iOS Safari fix
          overscrollBehavior: 'none',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          // iOS-specific fixes
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }
      }
    },
    Grid: {
      styles: {
        root: {
          margin: 0
        },
        col: {
          padding: 'var(--mantine-spacing-xs)'
        }
      }
    },
    TextInput: {
      styles: {
        input: {
          fontSize: '16px' // Prevents zoom on iOS
        }
      }
    },
    Select: {
      styles: {
        input: {
          fontSize: '16px' // Prevents zoom on iOS
        }
      }
    }
  },
  other: {
    // Custom CSS variables for video room
    cssVariables: {
      '--bg': 'var(--mantine-color-dark-8)',
      '--panel': 'var(--mantine-color-dark-7)',
      '--muted': 'var(--mantine-color-dark-3)',
      '--text': 'var(--mantine-color-dark-0)',
      '--accent': 'var(--mantine-color-blue-4)',
      '--danger': 'var(--mantine-color-red-6)',
      '--safe-area-inset-top': 'env(safe-area-inset-top, 0px)',
      '--safe-area-inset-right': 'env(safe-area-inset-right, 0px)',
      '--safe-area-inset-bottom': 'env(safe-area-inset-bottom, 0px)',
      '--safe-area-inset-left': 'env(safe-area-inset-left, 0px)'
    }
  }
});